/**
 * The org-admin plane's chrome (round9/design-pass — Direction A). Rendered
 * by ProtectedRoute scope="org" around every /organization/* page.
 *
 * Fixes a real gap the Direction A canvas called out: /organization/* had
 * no header, no nav and no Sign out. An org admin who wanted to leave had to
 * clear browser storage.
 *
 * The banner's tag is the organization's name and its second nav item uses
 * the backend's own tenant noun ("Add a builder" / "Add a location"), so
 * this shell reads the same two endpoints the overview page reads. That is
 * a deliberate redundant fetch, in the same posture ProtectedRoute's own
 * header documents for the trial banner: this SPA has no shared tenant/org
 * cache to plug into, every page fetches for itself, and adding one more
 * independent read is consistent with that rather than a new pattern. Both
 * reads are cheap, side-effect-free and already authorized; a failure here
 * is swallowed (except 401, which signs out like every other call site) and
 * the banner falls back to generic labels — it is chrome, never a gate.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../../contexts/auth-context";
import { ApiError } from "../../lib/api";
import { getMyOrg, listMyOrgTenants } from "../../lib/myOrgApi";
import { isOrgAdmin } from "../../lib/authRole";
import { SkipLink } from "../SkipLink";
import { PlaneBanner, type PlaneNavItem } from "./PlaneBanner";
import styles from "./OrgShell.module.css";

export function OrgShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const orgAdmin = user && isOrgAdmin(user) ? user : null;
  const idToken = orgAdmin?.idToken;

  const [orgName, setOrgName] = useState<string | null>(null);
  const [noun, setNoun] = useState<string | null>(null);

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    Promise.all([getMyOrg(idToken), listMyOrgTenants(idToken)])
      .then(([org, tenants]) => {
        if (cancelled) return;
        setOrgName(org.org.name ?? null);
        setNoun(tenants.tenantNoun);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        // Deliberately silent otherwise — see file header.
      });
    return () => { cancelled = true; };
  }, [idToken, signOut]);

  const items: PlaneNavItem[] = [
    { to: "/organization", label: "Overview", end: true },
    { to: "/organization/provision", label: noun ? `Add a ${noun}` : "Add to your organization" },
  ];

  return (
    <div className={styles.shell}>
      <SkipLink targetId="main-content" />
      <PlaneBanner
        tone="dark"
        tag={orgName ?? "Organization"}
        navLabel="Organization sections"
        items={items}
        identity={orgAdmin && <span className={styles.email}>{orgAdmin.email}</span>}
        onSignOut={signOut}
      />
      {children}
    </div>
  );
}
